import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, ImageUp, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const SCAN_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.DATA_MATRIX,
];

const REGION_ID = "serial-scan-region";

export default function SerialScanner({ open, onClose, onDetected }) {
  const scannerRef = useRef(null);
  const fileRef = useRef(null);
  const doneRef = useRef(false);
  const [status, setStatus] = useState("starting"); // starting | scanning | error
  const [ocrBusy, setOcrBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    doneRef.current = false;

    const start = async () => {
      setStatus("starting");
      // wait for the region element to mount
      await new Promise((r) => setTimeout(r, 120));
      if (cancelled) return;
      try {
        const scanner = new Html5Qrcode(REGION_ID, { formatsToSupport: SCAN_FORMATS, verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 }, aspectRatio: 1.6 },
          (decodedText) => {
            const val = (decodedText || "").trim();
            if (val) { handleDetected(val); }
          },
          () => {}
        );
        if (!cancelled) setStatus("scanning");
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    start();

    return () => {
      cancelled = true;
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopScanner = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { if (s.isScanning) await s.stop(); } catch { /* ignore */ }
      try { s.clear(); } catch { /* ignore */ }
    }
  };

  const handleDetected = async (result) => {
    if (doneRef.current) return;
    doneRef.current = true;
    await stopScanner();
    onDetected(typeof result === "string" ? { serial: result } : result);
    onClose();
  };

  const onPhotoPicked = async (fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Use a JPEG, PNG or WEBP photo");
      return;
    }
    setOcrBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/ocr/serial", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.found && (data.serial || data.manufacturer || data.model)) {
        const bits = [data.serial && `serial ${data.serial}`, data.manufacturer, data.model].filter(Boolean).join(" · ");
        toast.success(`Read ${bits}`);
        await handleDetected({ serial: data.serial || "", manufacturer: data.manufacturer || "", model: data.model || "" });
      } else {
        toast.error("Couldn't read a serial — try again or type it in");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read the photo");
    } finally {
      setOcrBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { stopScanner(); onClose(); } }}>
      <DialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-md" data-testid="serial-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-medium tracking-tight flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#F37B2A]" /> Scan serial
          </DialogTitle>
          <DialogDescription className="text-[#5A5A5A] text-sm">
            Point the camera at the barcode or QR code on the device label.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative w-full bg-black/80 border border-[#E5E0D5] overflow-hidden min-h-[220px]">
            {/* html5-qrcode owns this node's children — never render React children inside it */}
            <div id={REGION_ID} data-testid="serial-scan-region" className="w-full min-h-[220px]" />
            {status === "starting" && (
              <div className="absolute inset-0 pointer-events-none text-white/70 font-mono text-xs flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Starting camera…</div>
            )}
          </div>

          {status === "error" && (
            <div className="border border-[#E0B8A5] bg-[#FFF4EE] px-3 py-2 flex items-start gap-2" data-testid="scan-camera-error">
              <AlertTriangle className="w-4 h-4 text-[#B45309] mt-0.5" />
              <div className="text-sm text-[#3A3A3A]">
                Couldn&apos;t open the camera (permission denied or no camera). Use the photo option below, or type the serial manually.
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-[#E5E0D5]" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#9A9A9A]">or</span>
            <div className="flex-1 h-px bg-[#E5E0D5]" />
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            className="hidden"
            data-testid="serial-photo-input"
            onChange={(e) => onPhotoPicked(e.target.files)}
          />
          <Button
            type="button"
            variant="outline"
            data-testid="serial-photo-btn"
            disabled={ocrBusy}
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase"
          >
            {ocrBusy ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <ImageUp className="w-3.5 h-3.5 mr-2" />}
            {ocrBusy ? "Reading photo…" : "No barcode? Take a photo to read the text"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
